import { STAGEGROUPS_STAGES } from '@/constants'
import {
	Syllabus,
	SyllabusExternal,
	WebUiSyllabusTile,
} from '@/kontent/content-types'
import type { Mapping } from '@/types'
import { getLinkedItems } from '@/utils'
import { convertYearToNumeric } from '@/utils/stage_year'
import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import orderBy from 'lodash.orderby'
import { useContext } from 'react'
import { WebsiteConfigurationContext } from '../contexts/WebsiteConfigurationProvider'
import SyllabusColumn from './SyllabusColumn'

export type SyllabusTableProps = {
	items: WebUiSyllabusTile[]
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	onClearFilter: () => void
	isFiltered: boolean
	isSortByYear?: boolean
}

/**
 * Helper function to sort syllabus tiles alphabetically by title
 * Uses title_sort if available, otherwise falls back to left syllabus title
 */

const sortSyllabusTilesByTitle = (
	a: WebUiSyllabusTile,
	b: WebUiSyllabusTile,
	linkedItems: IContentItemsContainer,
) => {
	const aLeftSyllabuses = getLinkedItems(
		a.elements.syllabuses_left,
		linkedItems,
	)
	const bLeftSyllabuses = getLinkedItems(
		b.elements.syllabuses_left,
		linkedItems,
	)

	const aSortValue =
		a.elements.title_sort.value.length > 0
			? a.elements.title_sort.value
			: aLeftSyllabuses[0]?.elements.title.value || ''

	const bSortValue =
		b.elements.title_sort.value.length > 0
			? b.elements.title_sort.value
			: bLeftSyllabuses[0]?.elements.title.value || ''

	return aSortValue.localeCompare(bSortValue)
}

/**
 * Helper function to get the stage group order for sorting
 * Handles syllabuses that span multiple stage groups (e.g., K-10 = primary to secondary)
 * Uses STAGEGROUPS_STAGES constant to determine proper order
 */
const getStageGroupOrder = (syllabusTile: WebUiSyllabusTile): number => {
	const stageGroups = syllabusTile.elements.stages__stage_groups.value
	if (!stageGroups.length) return 999 // Put unclassified at the end

	// For syllabuses spanning multiple stage groups, use the earliest (lowest) stage group
	// This ensures K-6 (primary only) comes before K-10 (primary to secondary)
	const stageGroupOrder = Object.keys(STAGEGROUPS_STAGES) // ['primary', 'secondary', 'senior']
	const stageGroupIndices = stageGroups.map((group) =>
		stageGroupOrder.indexOf(group.codename),
	)
	return Math.min(...stageGroupIndices)
}

/**
 * Helper function to get the year range span for sorting
 * Uses STAGE_YEARS constant and handles spans like K-6, K-10, 7-10, 11-12
 */
const getYearRangeOrder = (syllabusTile: WebUiSyllabusTile): number => {
	const years = syllabusTile.elements.stages__stage_years.value
	if (!years.length) return 999

	// Convert year codenames to numeric values using the constants
	const numericYears = years.map((year) =>
		convertYearToNumeric(year.codename),
	)
	const minYear = Math.min(...numericYears)
	const maxYear = Math.max(...numericYears)

	// For year range ordering: prioritize by starting year, then by range span
	// K-6 (0-6) should come before K-10 (0-10) because it has smaller span
	return minYear * 100 + (maxYear - minYear) // e.g., K-6 = 0*100 + 6 = 6, K-10 = 0*100 + 10 = 10
}

/**
 * Helper function to check if a syllabus is senior (11-12)
 */
const isSeniorSyllabus = (syllabusTile: WebUiSyllabusTile): boolean => {
	const years = syllabusTile.elements.stages__stage_years.value
	const numericYears = years.map((year) =>
		convertYearToNumeric(year.codename),
	)
	return numericYears.some((year) => year >= 11)
}

function SyllabusTable({
	items: syllabusTiles,
	linkedItems,
	mappings,
	onClearFilter,
	isFiltered,
	isSortByYear = true,
}: SyllabusTableProps) {
	const { item: websiteConfiguration } =
		useContext(WebsiteConfigurationContext) || {}

	const leftColumnHeader =
		websiteConfiguration?.elements.kla_stage_page_left_column_title
			?.value || 'Current'
	const rightColumnHeader =
		websiteConfiguration?.elements.kla_stage_page_right_column_title
			?.value || 'Future'

	const isAllSyllabusRightEmpty = syllabusTiles.every(
		(syllabusTile) =>
			syllabusTile.elements.syllabuses_right.value.length === 0,
	)

	// Sort syllabus tiles using lodash orderBy with constants
	// Special handling: Senior syllabuses (11-12) are sorted alphabetically only
	// Non-senior syllabuses are sorted by stage group, then year range, then alphabetically
	const sortedSyllabusTiles = isSortByYear
		? orderBy(syllabusTiles, [
				// Primary sort: Stage group order using STAGEGROUPS_STAGES constant
				(tile) => getStageGroupOrder(tile),
				// Secondary sort: For non-senior, use year range; for senior, use 0 (alphabetical only)
				(tile) =>
					isSeniorSyllabus(tile) ? 0 : getYearRangeOrder(tile),
				// Tertiary sort: Always alphabetical by title
				// Priority: left syllabus title > title_sort (when left syllabus empty) > empty string
				(tile) => {
					const leftSyllabuses = getLinkedItems(
						tile.elements.syllabuses_left,
						linkedItems,
					)

					// If left syllabus exists, use its title
					if (leftSyllabuses[0]?.elements?.title?.value) {
						return leftSyllabuses[0].elements.title.value.toLowerCase()
					}

					// If left syllabus is empty, use title_sort
					if (tile.elements.title_sort.value.length > 0) {
						return tile.elements.title_sort.value.toLowerCase()
					}

					return ''
				},
		  ])
		: syllabusTiles.sort((a, b) =>
				sortSyllabusTilesByTitle(a, b, linkedItems),
		  )

	return (
		<div>
			{/* Header */}
			<div className="hidden lg:flex items-center border-b-2 pb-2.5 gap-4 border-[#22272B]">
				<div className="flex-1 font-bold">{leftColumnHeader}</div>
				{!isAllSyllabusRightEmpty && (
					<>
						<div className="w-[1px]"></div>
						<div className="flex-1 font-bold">
							{rightColumnHeader}
						</div>
					</>
				)}
			</div>
			{syllabusTiles.length === 0 && (
				<div className="flex flex-col md:flex-row items-center justify-center pt-[112px] pb-[48px] font-bold text-[20px] leading-7 px-4">
					We didn&rsquo;t find any results.&nbsp;
					<button
						type="button"
						className="text-[#002664] underline"
						onClick={() => onClearFilter()}
					>
						Clear all
					</button>
				</div>
			)}
			{/* Body */}
			{sortedSyllabusTiles.map((syllabusTile) => {
				const titleBlank = syllabusTile.elements.title_blank.value || ''
				const leftSyllabuses = getLinkedItems(
					syllabusTile.elements.syllabuses_left,
					linkedItems,
				) as (Syllabus | SyllabusExternal)[]

				const rightSyllabuses = getLinkedItems(
					syllabusTile.elements.syllabuses_right,
					linkedItems,
				) as (Syllabus | SyllabusExternal)[]

				return (
					<div
						className="rounded lg:rounded-none p-4 lg:p-0 lg:py-4 flex flex-col lg:flex-row gap-4 items-stretch border border-[#CDD3D6] lg:border-0 lg:border-b lg:border-[#22272B] mb-6 last:mb-0 lg:mb-0 lg:hover:bg-[#E5E9EF]"
						key={syllabusTile.system.id}
						data-id={syllabusTile.system.id}
					>
						{/* Left column */}
						<SyllabusColumn
							header={leftColumnHeader}
							syllabuses={leftSyllabuses}
							tooltipElement={
								syllabusTile.elements.syllabus_tooltip_left
							}
							linkedItems={linkedItems}
							mappings={mappings}
							isFiltered={isFiltered}
							titleBlank={titleBlank}
						/>

						{/* Right column */}
						{!isAllSyllabusRightEmpty && (
							<>
								<div className="bg-[#CDD3D6] w-full h-[1px] lg:h-auto lg:w-[1px] block"></div>
								<SyllabusColumn
									header={rightColumnHeader}
									syllabuses={rightSyllabuses}
									tooltipElement={
										syllabusTile.elements
											.syllabus_tooltip_right
									}
									linkedItems={linkedItems}
									mappings={mappings}
									isFiltered={isFiltered}
									titleBlank={titleBlank}
								/>
							</>
						)}
					</div>
				)
			})}
		</div>
	)
}

export default SyllabusTable
